import SEO from './../components/SEO';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Zap, CheckCircle, RefreshCw, CreditCard,
  Calendar, AlertCircle, XCircle, AlertTriangle,
} from 'lucide-react';
import { getBillingStatus, cancelSubscription } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

const PRO_FEATURES = [
  'Unlimited invoices every month',
  'Full invoice history & records',
  'Email invoices directly to clients',
  'Shareable invoice links',
  'CSV export & bulk PDF download',
];

export default function BillingPage() {
  const { user } = useAuth();
  const [billing, setBilling]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [showConfirm, setShowConfirm]   = useState(false);
  const [cancelling, setCancelling]     = useState(false);
  const [cancelError, setCancelError]   = useState('');
  const [cancelDone, setCancelDone]     = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDetails, setCancelDetails] = useState('');

  const CANCEL_REASONS = [
    'Too expensive',
    'Not using it enough',
    'Missing a feature I need',
    'Switching to another tool',
    'Just testing, not a real user',
    'Other',
  ];

  useEffect(() => {
    getBillingStatus()
      .then(data => {
        setBilling(data);
        if (data.cancelled) setCancelDone(true);
      })
      .catch(err => setError(err.message || 'Could not load billing info.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleCancel() {
    if (!cancelReason) return;
    setCancelling(true);
    setCancelError('');
    const fullReason = cancelDetails.trim()
      ? `${cancelReason} — ${cancelDetails.trim()}`
      : cancelReason;
    try {
      await cancelSubscription(fullReason);
      setBilling(prev => ({ ...prev, cancelled: true, dodo_subscription_id: null }));
      setCancelDone(true);
      setShowConfirm(false);
    } catch (err) {
      setCancelError(err.message || 'Could not cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const isPro      = billing?.plan === 'pro';
  const interval   = billing?.plan_interval;
  const expiresAt  = billing?.plan_expires_at;
  const isCancelled = billing?.cancelled || cancelDone;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO title="Billing | Invilic" description="Manage your Invilic subscription." canonical="https://www.invilic.com/billing" noindex={true} />
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 flex items-center gap-4">
          <Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gray-400" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Billing & Plan</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-6">

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Cancellation success banner */}
        {isCancelled && isPro && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Subscription cancelled</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                You still have Pro access until <strong>{formatDate(expiresAt)}</strong>. After that your account will move to the Free plan.
              </p>
            </div>
          </div>
        )}

        {/* Current plan */}
        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">Current Plan</h2>

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isPro ? 'bg-blue-50 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <Zap className={`w-5 h-5 ${isPro ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {isPro ? 'Pro' : 'Free'}
                  {isPro && interval && (
                    <span className="ml-2 text-xs font-medium text-gray-400 capitalize">({interval})</span>
                  )}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isPro
                    ? interval === 'yearly' ? '$60 / year' : '$6 / month'
                    : '5 invoices / month · Free forever'}
                </p>
              </div>
            </div>

            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              isCancelled
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                : isPro
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}>
              {isCancelled ? 'Cancelled' : isPro ? 'Active' : 'Free'}
            </span>
          </div>

          {isPro && expiresAt && (
            <div className="mt-5 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-3">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              {isCancelled
                ? <>Access until: <span className="font-medium text-gray-700 dark:text-gray-300 ml-1">{formatDate(expiresAt)}</span></>
                : <>Next renewal: <span className="font-medium text-gray-700 dark:text-gray-300 ml-1">{formatDate(expiresAt)}</span></>
              }
            </div>
          )}
        </section>

        {/* What's included */}
        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {isPro ? 'Your Pro features' : 'Included in Free'}
          </h2>
          <ul className="space-y-3">
            {isPro
              ? PRO_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {f}
                  </li>
                ))
              : (
                <>
                  {['5 invoices per month', 'All invoice templates', 'PDF download'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-800 mt-2">
                    <p className="text-sm text-gray-400 dark:text-gray-500 mb-3">Unlock with Pro:</p>
                    {PRO_FEATURES.map(f => (
                      <li key={f} className="flex items-center gap-3 text-sm text-gray-400 dark:text-gray-500 mb-2">
                        <CheckCircle className="w-4 h-4 text-gray-300 dark:text-gray-700 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </div>
                </>
              )}
          </ul>
        </section>

        {/* Actions */}
        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Actions</h2>

          {isPro ? (
            isCancelled ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your subscription is cancelled. You have full Pro access until <strong className="text-gray-700 dark:text-gray-300">{formatDate(expiresAt)}</strong>, after which your account moves to the Free plan automatically.
                </p>
              </div>
            ) : showConfirm ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Cancel your subscription?</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      You'll keep Pro access until <strong>{formatDate(expiresAt)}</strong>. No further charges will be made.
                    </p>
                  </div>
                </div>

                {/* Reason collection */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Why are you cancelling? <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      {CANCEL_REASONS.map(r => (
                        <label key={r} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="radio"
                            name="cancel_reason"
                            value={r}
                            checked={cancelReason === r}
                            onChange={() => setCancelReason(r)}
                            className="w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                            {r}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {cancelReason && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Any additional details? <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={cancelDetails}
                        onChange={e => setCancelDetails(e.target.value)}
                        placeholder="Help us understand what we could do better…"
                        rows={3}
                        maxLength={400}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 resize-none transition-colors"
                      />
                      {cancelDetails.length > 300 && (
                        <p className="text-xs text-gray-400 mt-1 text-right">{cancelDetails.length}/400</p>
                      )}
                    </div>
                  )}
                </div>

                {cancelError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
                )}

                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={handleCancel}
                    disabled={cancelling || !cancelReason}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {cancelling ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    {cancelling ? 'Cancelling…' : 'Confirm cancellation'}
                  </button>
                  <button
                    onClick={() => { setShowConfirm(false); setCancelError(''); setCancelReason(''); setCancelDetails(''); }}
                    disabled={cancelling}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Keep subscription
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You can cancel your subscription at any time. You'll keep Pro access until your current billing period ends.
                </p>
                <button
                  onClick={() => setShowConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel subscription
                </button>
              </div>
            )
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Upgrade to Pro to unlock unlimited invoices and all features.
              </p>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-md shadow-blue-600/20"
              >
                <Zap className="w-4 h-4" />
                Upgrade to Pro
              </Link>
            </div>
          )}

          <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Account: <span className="font-medium">{user?.email}</span>
            </p>
          </div>
        </section>

      </main>
    </div>
  );
}
